import { useEffect } from "react";
import Button from "@shared/v2/Button";
import TextButton from "@shared/v2/TextButton";
import Typography from "@shared/v2/Typography";
import { WandMagicSparklesSolidV6 } from "@shared/v2/Icomoon";
import Section from "./Section";
import { useFeatureFlags } from "../../reducers/layoutReducer/selectors";
import { useWizardContext } from "./TeamProductionWizardModal/WizardContext";

interface GoalWizardState {
  hideBanner?: boolean;
  status?: "not_started" | "in_progress" | "completed" | "skipped";
  year?: number;
}

interface SetGoalsBannerProps {
  agent: { value: string; label: string } | null;
  onCancel: () => void;
  onSetGoals: (year?: number) => void;
}

export const getTargetYear = (): number => {
  const date = new Date();
  const year = date.getFullYear();
  const cutOffDate = new Date(year, 8, 27);
  return date < cutOffDate ? year : year + 1;
};

const SetGoalsBanner = ({ onSetGoals }: SetGoalsBannerProps) => {
  const featureFlags = useFeatureFlags() as { goalWizard2: boolean };
  const wizardContext = useWizardContext() as unknown as {
    goalWizard: GoalWizardState;
    setGoalWizard: (value: GoalWizardState | ((prev: GoalWizardState) => GoalWizardState)) => void;
  } | null;

  if (!wizardContext) return null;

  const { goalWizard, setGoalWizard } = wizardContext;

  useEffect(() => {
    const targetYear = getTargetYear();

    setGoalWizard((prev) => {
      const resetStatus = !prev.status || (prev.year && prev.year < targetYear);

      return {
        ...(resetStatus ? {} : prev),
        status: resetStatus ? "not_started" : prev.status,
        year: targetYear,
      };
    });
  }, [setGoalWizard]);

  if (
    !featureFlags.goalWizard2 ||
    goalWizard.hideBanner ||
    goalWizard.status === "completed" ||
    goalWizard.status === "skipped"
  ) {
    return null;
  }

  return (
    <Section className="!tw-py-[16px] tw-flex tw-items-center tw-gap-[24px] tw-border-[2px] tw-border-theme-primary">
      <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[4px]">
        {/* @ts-expect-error props */}
        <Typography variant="headline2">
          {goalWizard.status === "in_progress"
            ? "Pick up where you left off"
            : `It's time to set your goals for ${goalWizard.year}!`}
        </Typography>
        {/* @ts-expect-error props */}
        <Typography className="!tw-text-gray-50">
          {goalWizard.status === "in_progress"
            ? "Click to finish setting your goals."
            : "Input how much you want to make next year and Brivity will take care of the rest"}
        </Typography>
      </div>
      {goalWizard.status !== "in_progress" && (
        // @ts-expect-error props
        <TextButton
          size="medium"
          className="tw-mr-[-6px]"
          onClick={() => setGoalWizard({ status: "skipped" })}
        >
          I Want to Set My Goals Myself
        </TextButton>
      )}
      {goalWizard.status === "in_progress" && (
        // @ts-expect-error props
        <TextButton
          size="medium"
          className="tw-mr-[-6px]"
          onClick={() => setGoalWizard({ hideBanner: true })}
        >
          Dismiss
        </TextButton>
      )}
      {/* @ts-expect-error props */}
      <Button
        size="medium"
        className="tw-flex tw-items-center tw-gap-[4px]"
        onClick={() => onSetGoals(goalWizard.year)}
      >
        <WandMagicSparklesSolidV6 />{" "}
        {goalWizard.status === "in_progress" ? "Resume Business Planning" : "Help Me Set My Goals"}
      </Button>
    </Section>
  );
};

export default SetGoalsBanner;
